import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  MatCheckboxModule,
  MatCheckboxChange,
} from '@angular/material/checkbox'

import { tap, catchError, of, filter, switchMap } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import {
  UserService,
  PrivacySettingType,
} from '../../../services/user/user.service'
import { getTitle } from '../../../util/accessibility'
import { ButtonComponent } from '../../../components/button/button.component'
import { FormsModule } from '@angular/forms'
import { Address4, Address6 } from 'ip-address'
import { captureException } from '../../../util/captureException'

interface PrivacySettingOption {
  type: PrivacySettingType
  title: string
  description: string
}

@UntilDestroy()
@Component({
  selector: 'app-organization-setting-page',
  standalone: true,
  imports: [ButtonComponent, FormsModule, MatIcon, MatCheckboxModule],
  templateUrl: './organization-setting-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './organization-setting-page.component.scss',
})
export class OrganizationSettingPageComponent implements OnInit {
  privacySettingOptions: PrivacySettingOption[] = [
    {
      type: 'FULLY_OPEN',
      title: 'Fully Open',
      description: '全ユーザーが全ユーザーのデータを見ることが可能です。',
    },
    {
      type: 'PARTIALLY_OPEN',
      title: 'Partially Open',
      description:
        '自分のデータ、自分のマネジメントチェーン、直属の上司を共有しているユーザー、直属の上司を共有しているユーザーのマネジメントチェーンを見ることが可能です。',
    },
    {
      type: 'FULLY_CLOSED',
      title: 'Fully Closed',
      description:
        '全ユーザーが、自分と自分の部下のデータのみ見ることが可能です。',
    },
  ]
  selectedPrivacySetting: PrivacySettingType | undefined = undefined
  requiredMfa: boolean = false
  allowedIPAddressesText: string = ''
  constructor(
    private messageDialogService: MessageDialogService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private titleService: Title,
  ) {
    this.titleService.setTitle(getTitle('組織管理'))
  }

  ngOnInit(): void {
    this.userService
      .getAdminOrg()
      .pipe(
        tap((adminOrg) => {
          this.selectedPrivacySetting = adminOrg.privacySetting
          this.requiredMfa = adminOrg.requiredMfa
        }),
        switchMap(() => {
          return this.userService.getAllowedIPAddresses()
        }),
        tap((allowedIPAddresses) => {
          this.allowedIPAddressesText = allowedIPAddresses.join('\n')
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  onPrivacySettingChange(setting: PrivacySettingType): void {
    if (setting === this.selectedPrivacySetting) {
      return
    }

    this.selectedPrivacySetting = setting
    this.userService
      .updatePrivacySetting(setting)
      .pipe(
        tap(() => {
          this.snackBar.open('プライバシー設定を更新しました。', 'close', {
            duration: 3000,
          })
        }),
        catchError((error) => {
          captureException(error)
          this.messageDialogService.showError(
            'プライバシー設定の更新に失敗しました。時間を置いて再度お試しください。',
            {
              title: 'エラー',
            },
          )
          return of()
        }),
      )
      .subscribe()
  }

  onMfaChange(event: MatCheckboxChange): void {
    this.requiredMfa = event.checked

    this.messageDialogService
      .showConfirm(
        event.checked
          ? '必須にすると、組織内のユーザーはログアウトされ、次回ログイン時に二要素認証の登録を求められます。適用しますか？'
          : '任意にしても、ユーザーが登録済みの二要素認証は残ります。適用しますか？',
        {
          primaryButtonText: '適用',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(
        switchMap((result) => {
          if (result !== DialogResult.PrimaryButtonClicked) {
            event.source.checked = !event.checked
            return of()
          }

          return this.userService.updateRequiredMfa(event.checked).pipe(
            tap(() => {
              this.snackBar.open('二要素認証を更新しました。', 'close', {
                duration: 3000,
              })
            }),
            catchError((error) => {
              captureException(error)
              event.source.checked = !event.checked
              this.messageDialogService.showError(
                '二要素認証の更新に失敗しました。時間を置いて再度お試しください。',
                {
                  title: 'エラー',
                },
              )
              return of()
            }),
          )
        }),
      )
      .subscribe()
  }

  saveIpAddressControl() {
    const allowedIPAddresses = this.allowedIPAddressesText
      .split('\n')
      .map((ip) => ip.trim())
      .filter((ip) => ip.length > 0)

    // バリデーション
    const invalidAddresses = allowedIPAddresses.filter(
      (ip) => !this.isValidCidr(ip),
    )

    if (invalidAddresses.length > 0) {
      this.messageDialogService.showError(
        `無効なIPアドレスが含まれています: ${invalidAddresses.join(', ')}`,
        {
          title: 'エラー',
        },
      )
      return
    }

    this.messageDialogService
      .showConfirm(
        `本当にIP制限を適用してもよろしいですか？
        誤ったIPアドレスを設定すると、全てのユーザーがログインできなくなります。`,
        {
          primaryButtonText: '適用',
          secondaryButtonText: 'キャンセル',
        },
      )
      .pipe(
        filter((result) => result === DialogResult.PrimaryButtonClicked),
        switchMap(() => {
          return this.userService.saveAllowedIPAddresses(
            this.allowedIPAddressesText
              .split('\n')
              .map((ip) => ip.trim())
              .filter((ip) => ip.length > 0),
          )
        }),
        tap(() => {
          this.snackBar.open('適用しました', 'close', {
            duration: 3000,
          })
        }),
      )
      .subscribe()
  }

  private isValidCidr(cidr: string): boolean {
    try {
      if (cidr.includes(':')) {
        new Address6(cidr) // throws if invalid
      } else {
        new Address4(cidr)
      }
      return true
    } catch {
      return false
    }
  }
}
